
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import _debounce from 'lodash/debounce';

import {
  AutocompleteItem,
  CatalogProductSearchTypeEnum,
} from '@/common/services/swagger/index.defs';
import { ComponentName } from '@/catalogs/api/configuration/components/ComponentName';
import { SearchProductsCommand } from '@/catalogs/api/runtime/CommandExecutor';
import { setupComponent } from '@/catalogs/composables/setupComponent';

import InjectStyles from '@/common/components/InjectStyles.vue';
import Btn from '@/common/components/Btn.vue';
import FormInputClearable from '@/common/components/FormInputClearable.vue';
import Dropdown from '@/common/components/Dropdown.vue';
import Badge from '@/common/components/Badge.vue';
import Clickable from '@/common/components/Clickable.vue';
import Spinner from '@/common/components/Spinner.vue';
import { Autocomplete, AutocompleteModelItem } from '@/products/api/runtime/Search';
import Highlighter from 'vue-highlight-words';
import VRuntimeTemplate from 'vue3-runtime-template';
import { IProductsSearchFormOptions } from '@/catalogs/api/configuration/components/IProductsSearchFormOptions';
import Image from '@/common/components/Image.vue';

const searchTypeAllWords = {
  value: CatalogProductSearchTypeEnum.WithAllWords,
  translate: 'SearchTypeAllWords',
};
const searchTypeOneWord = {
  value: CatalogProductSearchTypeEnum.WithAtLeastOneWord,
  translate: 'SearchTypeOneWord',
};
const searchTypeExact = {
  value: CatalogProductSearchTypeEnum.ExactPhrase,
  translate: 'SearchTypeExact',
};

export default defineComponent({
  name: ComponentName.productsSearchForm,

  components: {
    InjectStyles,
    Btn,
    Dropdown,
    FormInputClearable,
    Badge,
    Clickable,
    Spinner,
    Highlighter,
    VRuntimeTemplate,
    Image,
  },

  props: {
    instanceId: String,
  },

  setup(props) {
    const root = ref<HTMLElement>();

    const searchModel = reactive({
      text: '',
      type: CatalogProductSearchTypeEnum.WithAllWords,
      isDirty: false,
    });

    const autocomplete = ref<Autocomplete>({
      totalCount: 0,
      visibleMatchCount: 0,
      items: [] as AutocompleteModelItem[],
    });
    const isAutocompleteVisible = ref(false);
    const isAutocompleteLoading = ref(false);

    const {
      componentName,
      isReady,
      isWebComponent,
      instance,
      store,
      routeData,
      widerThan,
      narrowerThan,
      t,
      emit,
    } = setupComponent(root, props.instanceId);

    const componentOptions = computed<IProductsSearchFormOptions | undefined>(
      () => store.value?.options.components?.productsSearchForm,
    );

    const visible = computed(() => !!componentOptions.value?.visible);

    const autocompleteEnabled = computed(() => !!componentOptions.value?.useAutocomplete);

    const baseSearchTypeOptions = [searchTypeAllWords, searchTypeOneWord, searchTypeExact];

    const searchTypeOptions = computed(() => {
      return baseSearchTypeOptions;
    });

    const onSearch = async (resetCategories?: boolean, resetFilters?: boolean) => {
      isAutocompleteVisible.value = false;

      const resetFiltersValue = resetFilters || autocomplete.value.matchesOnlyWithoutFilters;
      const resetCategoriesValue = resetCategories || resetFiltersValue;

      try {
        await instance.value?.execute(
          new SearchProductsCommand({
            searchText: searchModel.text,
            searchType: searchModel.type,
            resetCategories: resetCategoriesValue,
            resetFilters: resetFiltersValue,
          }),
        );

        searchModel.isDirty = false;
      } catch (error) {
        // Ignore Error
      }
    };

    const searchText = (text: string) => {
      searchModel.text = text;
      onSearch();
    };

    const onClear = () => {
      if (!searchModel.isDirty) {
        searchModel.text = '';
        autocomplete.value.items = [];
        autocomplete.value.totalCount = 0;

        onSearch(false);
      }
    };

    const openAutocomplete = () => {
      if (!autocompleteEnabled.value) {
        return;
      }

      if (searchModel.text == routeData.value?.products.searchText) {
        return;
      }

      if (searchModel.text.length > 2) {
        isAutocompleteVisible.value = true;
      }
    };

    const closeAutocomplete = () => {
      isAutocompleteVisible.value = false;
    };

    const handleAutocompleteSearch = _debounce(async (text) => {
      if (!autocompleteEnabled.value) {
        return;
      }

      const hasText: boolean = text != null && text.length > 2;
      isAutocompleteVisible.value = hasText;

      if (hasText) {
        isAutocompleteLoading.value = true;
        const items: Autocomplete['items'] = [];
        const result = await instance.value?.httpService?.autocomplete(text);
        if (result) {
          const matches: Autocomplete['items'] = (result.items || []).map(
            (match: AutocompleteItem) => ({
              ...match,
              text: getFirstAttributeValue(match, '#DisplayName') || '',
              secondaryText: getAllIds(match) || '',
              description: getFirstAttributeValue(match, 'CatalogDescription') || '',
              images: match.images,
              type: 'item',
              location: 'bottom',
            }),
          );

          const autocompleteValue: Autocomplete = {
            totalCount: result.totalCount || 0,
            visibleMatchCount: result.matches?.length || 0,
            items: [],
          };

          const isRoot =
            !routeData.value?.products?.cid || routeData.value?.products?.cid === 'ROOT';

          if (matches.length) {
            items.push({
              attributes: {},
              productId: '',
              count: result.totalCount,
              type: isRoot ? 'root' : 'current',
              location: 'bottom',
              categoryName: result.autoCategoryNavigationCategory?.name,
              sparePart: false,
              exactMatch: false,
            });
          }

          if (result.totalCount != result.rootTotalCount) {
            items.push({
              attributes: {},
              productId: '',
              count: result.rootTotalCount,
              type: 'root',
              location: 'bottom',
              sparePart: false,
              exactMatch: false,
            });
          }

          if (result.noFiltersTotalCount > 0) {
            items.push({
              attributes: {},
              productId: '',
              count: result.noFiltersTotalCount,
              type: 'no-filters',
              location: 'bottom',
              sparePart: false,
              exactMatch: false,
            });
          }

          autocompleteValue.items = items.concat(matches);
          autocompleteValue.matchesOnlyInAllCategories =
            result.totalCount == 0 && result.rootTotalCount > 0;
          autocompleteValue.matchesOnlyWithoutFilters = result.noFiltersTotalCount > 0;
          autocomplete.value = autocompleteValue;
        }

        isAutocompleteLoading.value = false;
      }
    }, 300);

    const onSearchInput = () => {
      //searchModel.isDirty = searchModel.text.length > 0;
      searchModel.isDirty =
        searchModel.text?.length > 0 && searchModel.text != routeData.value?.products.searchText;
      handleAutocompleteSearch(searchModel.text);
    };

    const getFirstAttributeValue = (item: AutocompleteItem, attributeCode: string) => {
      if (item.attributes) {
        var attr = item.attributes[attributeCode];

        if (attr && attr.values) {
          return attr.values[0].text;
        }
      }

      return undefined;
    };

    const getAllIds = (item: AutocompleteItem) => {
      if (item.attributes) {
        var attr = item.attributes['#AllIdentifiers'];

        if (attr && attr.values) {
          return attr.values.map((a) => a.text).join(', ');
        }
      }

      return undefined;
    };

    // Sync the search model text with route search text after initializing the app.
    watch(
      () => routeData.value?.products.searchText,
      (text, oldText) => {
        if (text !== oldText) {
          searchModel.text = text ?? '';
        }
      },
    );

    watch(
      () => routeData.value?.products.searchType,
      (type, oldType) => {
        if (type && type !== oldType) {
          searchModel.type = type;
        }
      },
    );

    watch(
      () => searchModel.type,
      () => {
        if (routeData.value) {
          routeData.value.products.searchType = searchModel.type;
        }
      },
    );

    const highlightTerms = computed(() => {
      if (!searchModel.text) {
        return [];
      }

      const arr = searchModel.text
        .trim()
        .toLowerCase()
        .split(/[._-\s/]+/);

      arr.push(searchModel.text.trim());

      // extract numbers
      const numbers = searchModel.text.match(/\d+/g);
      let txt = searchModel.text;

      if (numbers) {
        numbers.forEach((m) => {
          arr.push(m);
          txt = txt.replace(m, '$');
        });
      }

      const arr2 = txt.trim().toLowerCase().split('$');

      arr.push(...arr2);

      return arr;
    });

    const getThumbnailUrl = (item: AutocompleteItem) => {
      if (item.images && item.images.length > 0) {
        return item.images[0].thumbnailUrl;
      }

      return undefined;
    };

    return {
      root,
      componentName,
      isReady,
      isWebComponent,
      store,
      searchModel,
      searchTypeOptions,
      autocomplete,
      isAutocompleteVisible,
      isAutocompleteLoading,
      highlightTerms,
      widerThan,
      narrowerThan,
      t,
      onSearch,
      onSearchInput,
      onClear,
      searchText,
      openAutocomplete,
      closeAutocomplete,
      visible,
      componentOptions,
      instance,
      emit,
      getThumbnailUrl,
    };
  },
});
